import { Avatar, AvatarFallback, Button, Modal, toast } from "@devhop/ui";
import { PencilIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import { getInitials } from "@/shared/lib/utils";
import type { ShopDetail } from "../api/client";
import { useAddShopStaff, useUpdateShopStaff } from "../api/queries";
import { AddStaffForm } from "./AddStaffForm";
import { EditStaffForm } from "./EditStaffForm";

const ROLE_LABELS: Record<string, string> = {
  owner: "ເຈົ້າຂອງຮ້ານ",
  manager: "ຜູ້ຈັດການ",
  cashier: "ເກັບເງິນ",
};

type ShopStaffTableProps = {
  staff: ShopDetail["staff"];
  shopId: string;
  canAddStaff?: boolean;
};

export function ShopStaffTable({
  staff,
  shopId,
  canAddStaff = false,
}: ShopStaffTableProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<
    ShopDetail["staff"][number] | null
  >(null);
  const addStaff = useAddShopStaff(shopId);
  const updateStaff = useUpdateShopStaff(shopId);

  return (
    <>
      {canAddStaff && (
        <div className="mb-3 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAddOpen(true)}
            className="border-[#004799] text-[#004799] hover:bg-[#004799]/10"
          >
            <PlusIcon className="mr-2 size-4" />
            ເພີ່ມພະນັກງານ
          </Button>
        </div>
      )}

      <Modal
        open={addOpen}
        onOpenChange={setAddOpen}
        title="ເພີ່ມພະນັກງານ"
        description="ສ້າງຜູ້ໃຊ້ໃໝ່ແລະເພີ່ມເຂົ້າຮ້ານພ້ອມກຳນົດບົດບາດ (ສິດຕາມ role ຈະຖືກ preset ໃຫ້ອັດຕະໂນມັດ)"
        size="sm"
      >
        <AddStaffForm
          submitting={addStaff.isPending}
          onSubmit={async (vals) => {
            try {
              await addStaff.mutateAsync({
                email: vals.email,
                name: vals.name,
                password: vals.password,
                staffRole: vals.staffRole,
              });
              setAddOpen(false);
              toast.success("ເພີ່ມພະນັກງານສຳເລັດ");
            } catch {
              // fetcher ສະແດງ toast ແລ້ວ, ບໍ່ປິດ modal ເພື່ອໃຫ້ຜູ້ໃຊ້ລອງໃໝ່ໄດ້
            }
          }}
        />
      </Modal>

      <Modal
        open={!!editingStaff}
        onOpenChange={(open) => {
          if (!open) setEditingStaff(null);
        }}
        title="ແກ້ໄຂພະນັກງານ"
        description="ປັບປຸງຊື່, ອີເມວ ຫຼືຕັ້ງລະຫັດຜ່ານໃໝ່"
        size="sm"
      >
        {editingStaff && (
          <EditStaffForm
            initialValues={{
              email: editingStaff.userEmail,
              name: editingStaff.userName ?? "",
            }}
            submitting={updateStaff.isPending}
            onSubmit={async (vals) => {
              try {
                await updateStaff.mutateAsync({
                  staffId: editingStaff.id,
                  input: {
                    email: vals.email,
                    name: vals.name,
                    password: vals.password,
                  },
                });
                setEditingStaff(null);
                toast.success("ແກ້ໄຂພະນັກງານສຳເລັດ");
              } catch {
                // fetcher ສະແດງ toast ແລ້ວ, ຄ້າງ modal ໄວ້ໃຫ້ແກ້ໄຂຕໍ່
              }
            }}
          />
        )}
      </Modal>

      {!staff || staff.length === 0 ? (
        <p className="text-muted-foreground text-sm">ບໍ່ມີພະນັກງານ</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-2 text-left font-medium">ຊື່</th>
                <th className="py-2 text-left font-medium">ອີເມວ</th>
                <th className="py-2 text-left font-medium">ບົດບາດ</th>
                {canAddStaff && (
                  <th className="py-2 text-right font-medium">ການກະທໍາ</th>
                )}
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} className="border-b last:border-0">
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <Avatar className="size-8">
                        <AvatarFallback className="text-xs">
                          {getInitials(s.userName ?? s.userId)}
                        </AvatarFallback>
                      </Avatar>
                      <span>{s.userName ?? s.userId}</span>
                    </div>
                  </td>
                  <td className="py-2 text-muted-foreground">{s.userEmail}</td>
                  <td className="py-2 text-muted-foreground">
                    {ROLE_LABELS[s.staffRole] ?? s.staffRole}
                  </td>
                  {canAddStaff && (
                    <td className="py-2 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingStaff(s)}
                        title="ແກ້ໄຂ"
                        aria-label="ແກ້ໄຂ"
                      >
                        <PencilIcon className="size-4" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
